import React, { useState, useEffect } from 'react';
import { Shield, Phone, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

const PhoneLogin = () => {
    const { sendOTP, verifyOTP, error } = useAuth();
    const { t, language, toggleLanguage } = useLanguage();
    
    const [step, setStep] = useState('phone'); // phone, otp, success
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [demoOtp, setDemoOtp] = useState('');

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const formatPhone = (value) => {
        let digits = value.replace(/\D/g, '');
        if (digits.startsWith('91')) digits = digits.substring(2);
        if (digits.length > 10) digits = digits.substring(0, 10);
        return digits.length > 0 ? `+91${digits}` : '';
    };

    const handlePhoneChange = (e) => {
        setPhone(formatPhone(e.target.value));
    };

    const handleSendOTP = async () => {
        setLoading(true);
        try {
            const result = await sendOTP(phone);
            setDemoOtp(result.demo_otp || '');
            setStep('otp');
            setCountdown(60);
        } catch (err) {
            // Error handled by context
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        setLoading(true);
        try {
            await verifyOTP(phone, otp);
            setStep('success');
        } catch (err) {
            // Error handled by context
        } finally {
            setLoading(false);
        }
    };

    const handleOTPChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').substring(0, 6);
        setOtp(value);
    };

    if (step === 'success') {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="w-full max-w-md" data-testid="login-success-card">
                    <CardContent className="pt-8 pb-8 flex flex-col items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                        </div>
                        <h2 className="text-xl font-bold text-center">{t('success')}!</h2>
                        <p className="text-muted-foreground text-center">
                            {language === 'en' ? 'Redirecting to dashboard...' : 'डैशबोर्ड पर जा रहे हैं...'}
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-slate-900 dark:to-slate-800 flex flex-col">
            {/* Language Toggle */}
            <div className="p-4 flex justify-end">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleLanguage}
                    data-testid="language-toggle"
                    className="font-medium"
                >
                    {language === 'en' ? 'हिंदी' : 'English'}
                </Button>
            </div>

            {/* Hero Section */}
            <div className="flex-1 flex flex-col items-center justify-center p-4">
                {/* Logo */}
                <div className="mb-8 flex flex-col items-center">
                    <div className="w-20 h-20 rounded-2xl bg-primary flex items-center justify-center shadow-lg mb-4">
                        <Shield className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold font-heading text-foreground">ShieldPayments</h1>
                    <p className="text-muted-foreground mt-2 text-center">
                        {language === 'en' 
                            ? 'AI-Powered Payment Safety for Your Business' 
                            : 'आपके व्यापार के लिए AI-संचालित भुगतान सुरक्षा'}
                    </p>
                </div>

                {/* Login Card */}
                <Card className="w-full max-w-md" data-testid="login-card">
                    <CardHeader className="text-center">
                        <CardTitle className="flex items-center justify-center gap-2">
                            <Phone className="w-5 h-5" />
                            {step === 'phone' ? t('enterPhone') : t('enterOTP')}
                        </CardTitle>
                        <CardDescription>
                            {step === 'phone' ? t('phoneHint') : `OTP sent to ${phone}`}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {error && (
                            <Alert variant="destructive" data-testid="login-error">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        {/* Demo OTP Notice */}
                        {demoOtp && step === 'otp' && (
                            <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                                <AlertDescription className="text-blue-700 dark:text-blue-300">
                                    Demo OTP: <span className="font-mono font-bold">{demoOtp}</span>
                                </AlertDescription>
                            </Alert>
                        )}

                        {step === 'phone' ? (
                            <>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">{language === 'en' ? 'Phone Number' : 'फोन नंबर'}</label>
                                    <Input
                                        type="tel"
                                        placeholder="+91 98765 43210"
                                        value={phone}
                                        onChange={handlePhoneChange}
                                        data-testid="phone-input"
                                        className="h-12 text-lg"
                                        disabled={loading}
                                    />
                                </div>
                                <Button
                                    onClick={handleSendOTP}
                                    disabled={phone.length < 13 || loading}
                                    className="w-full h-12 rounded-full"
                                    data-testid="send-otp-btn"
                                >
                                    {loading ? (
                                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                    ) : (
                                        <ArrowRight className="w-5 h-5 mr-2" />
                                    )}
                                    {loading ? t('loading') : t('sendOTP')}
                                </Button>
                            </>
                        ) : (
                            <>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">{language === 'en' ? 'Enter 6-digit OTP' : '6-अंकों का OTP दर्ज करें'}</label>
                                    <Input
                                        type="text"
                                        placeholder="000000"
                                        value={otp}
                                        onChange={handleOTPChange}
                                        data-testid="otp-input"
                                        className="h-14 text-2xl text-center tracking-[0.5em] font-mono"
                                        maxLength={6}
                                        disabled={loading}
                                    />
                                </div>
                                <Button
                                    onClick={handleVerifyOTP}
                                    disabled={otp.length !== 6 || loading}
                                    className="w-full h-12 rounded-full"
                                    data-testid="verify-otp-btn"
                                >
                                    {loading ? (
                                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                    ) : (
                                        <CheckCircle2 className="w-5 h-5 mr-2" />
                                    )}
                                    {loading ? t('loading') : t('verifyOTP')}
                                </Button>
                                <div className="flex items-center justify-between">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            setStep('phone');
                                            setOtp('');
                                            setDemoOtp('');
                                        }}
                                        data-testid="change-phone-btn"
                                    >
                                        {language === 'en' ? 'Change Number' : 'नंबर बदलें'}
                                    </Button>
                                    {countdown > 0 ? (
                                        <span className="text-sm text-muted-foreground">
                                            {t('resendOTP')} ({countdown}s)
                                        </span>
                                    ) : (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleSendOTP}
                                            data-testid="resend-otp-btn"
                                        >
                                            {t('resendOTP')}
                                        </Button>
                                    )}
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Disclaimer */}
                <div className="mt-6 px-4 max-w-md">
                    <p className="disclaimer-banner text-center">
                        {t('disclaimer')}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PhoneLogin;
